import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api.js";
import { AppPolicyCard } from "./AppPolicyCard.js";
import { supabase } from "../lib/supabaseClient.js";
import { API_BASE_URL } from "../env.js";
import type { Release } from "../lib/types.js";

/** 8MB parts: comfortably above R2's 5MB minimum, small enough that each request is quick and a
 * failure costs one part, not the whole file. A 60MB MSI is 8 parts. */
const PART_BYTES = 8 * 1024 * 1024;
/** Per-request ceiling so a stalled part surfaces as an error instead of hanging at "100%". */
const PART_TIMEOUT_MS = 90_000;

/** One XHR (so we get byte-level upload progress), resolving to the parsed JSON body. */
function xhrJson<T>(method: string, url: string, token: string, body: XMLHttpRequestBodyInit | null,
  contentType: string, onProgress?: (loaded: number) => void): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url);
    xhr.timeout = PART_TIMEOUT_MS;
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.setRequestHeader("Content-Type", contentType);
    if (onProgress) xhr.upload.onprogress = (e) => onProgress(e.loaded);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText) as T); }
        catch { reject(new Error("Unexpected server response")); }
        return;
      }
      let message = `Upload failed (${xhr.status})`;
      try {
        const parsed = JSON.parse(xhr.responseText) as { error?: string };
        if (parsed.error) message = parsed.error;
      } catch { /* keep default */ }
      reject(new Error(message));
    };
    xhr.onerror = () => reject(new Error("Network error during upload"));
    xhr.ontimeout = () => reject(new Error("The upload stalled (no reply for 90 seconds). Check your connection and try again."));
    xhr.onabort = () => reject(new Error("Upload cancelled"));
    xhr.send(body);
  });
}

/**
 * Uploads the MSI to R2 in 8MB parts through the multipart endpoint (see
 * functions/api/admin/releases/multipart.ts) and returns the stored object path. Each part is
 * its own short request with real progress; on any failure the multipart upload is aborted so
 * no half-written object is left behind.
 */
async function uploadMsiToR2(file: File, version: string, onProgress: (pct: number, detail: string) => void): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("Not signed in");

  const base = `${API_BASE_URL}/admin/releases/multipart`;
  const q = (params: Record<string, string | number>) =>
    Object.entries(params).map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join("&");

  const { uploadId, key } = await xhrJson<{ uploadId: string; key: string }>(
    "POST", `${base}?${q({ action: "start", version, fileName: file.name })}`, token, null, "application/json");

  const totalParts = Math.max(1, Math.ceil(file.size / PART_BYTES));
  const parts: { partNumber: number; etag: string }[] = [];
  try {
    for (let i = 0; i < totalParts; i++) {
      const partNumber = i + 1;
      const chunk = file.slice(i * PART_BYTES, Math.min(file.size, (i + 1) * PART_BYTES));
      const done = i * PART_BYTES;
      onProgress(Math.round((done / file.size) * 100), `part ${partNumber} of ${totalParts}`);
      const part = await xhrJson<{ partNumber: number; etag: string }>(
        "POST", `${base}?${q({ action: "part", key, uploadId, partNumber })}`, token, chunk, "application/octet-stream",
        (loaded) => onProgress(Math.min(99, Math.round(((done + loaded) / file.size) * 100)), `part ${partNumber} of ${totalParts}`));
      parts.push(part);
    }
    onProgress(100, "finishing");
    const result = await xhrJson<{ filePath: string }>(
      "POST", `${base}?${q({ action: "complete", key, uploadId })}`, token, JSON.stringify({ parts }), "application/json");
    return result.filePath;
  } catch (err) {
    // Best-effort clean-up; the original error is what the user needs to see.
    try {
      await xhrJson("POST", `${base}?${q({ action: "abort", key, uploadId })}`, token, null, "application/json");
    } catch { /* ignore */ }
    throw err;
  }
}

/** SHA-256 of the selected file, computed in-browser via Web Crypto so the
 * server-recorded checksum reflects exactly the bytes that were uploaded. */
async function sha256Hex(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export function DownloadManager() {
  const [releases, setReleases] = useState<Release[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      const res = await api.get<{ releases: Release[] }>("/admin/releases");
      setReleases(res.releases);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load releases");
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function setLatest(id: string) {
    try {
      await api.post(`/admin/releases/${id}/set-latest`);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to set latest");
    }
  }

  return (
    <div>
      <h1>Download Manager</h1>
      <AppPolicyCard />
      <UploadForm
        onDone={() => {
          load();
        }}
      />
      {error && <p className="error-text">{error}</p>}
      {releases && (
        <table className="data-table">
          <thead>
            <tr>
              <th>Version</th>
              <th>Released</th>
              <th>Size</th>
              <th>Latest</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {releases.map((r) => (
              <ReleaseRow key={r.id} release={r} onSetLatest={setLatest} onChanged={load} onError={setError} />
            ))}
            {releases.length === 0 && (
              <tr>
                <td colSpan={5}>No releases yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ReleaseRow({
  release,
  onSetLatest,
  onChanged,
  onError,
}: {
  release: Release;
  onSetLatest: (id: string) => void;
  onChanged: () => void;
  onError: (msg: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [version, setVersion] = useState(release.version);
  const [busy, setBusy] = useState(false);
  const [replaceStatus, setReplaceStatus] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  /** "Replace file": swap this release's installer for a freshly picked MSI, keeping the version
   * number and its latest flag. Same multipart upload as the form, then the replace record call. */
  async function replaceFile(picked: File | null) {
    if (fileInput.current) fileInput.current.value = ""; // allow re-picking the same file later
    if (!picked) return;
    if (!window.confirm(`Replace the installer for version ${release.version} with "${picked.name}"?`)) return;
    setBusy(true);
    onError(null);
    try {
      setReplaceStatus("Hashing…");
      const sha256 = await sha256Hex(picked);
      const filePath = await uploadMsiToR2(picked, release.version, (pct, detail) => {
        setReplaceStatus(`Uploading ${pct}% (${detail})`);
      });
      setReplaceStatus("Recording…");
      await api.post("/admin/releases", {
        version: release.version,
        filePath,
        fileSize: picked.size,
        sha256,
        markLatest: release.is_latest,
        replace: true,
      });
      onChanged();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Replace failed");
    } finally {
      setReplaceStatus(null);
      setBusy(false);
    }
  }

  async function save() {
    setBusy(true);
    onError(null);
    try {
      await api.patch(`/admin/releases/${release.id}`, { version });
      setEditing(false);
      onChanged();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to save release");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!confirm(`Delete version ${release.version}? This removes the installer and cannot be undone.`)) return;
    setBusy(true);
    onError(null);
    try {
      await api.delete(`/admin/releases/${release.id}`);
      onChanged();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to delete release";
      onError(
        msg === "cannot_delete_latest"
          ? "Can't delete the latest release — mark another version as latest first."
          : msg
      );
    } finally {
      setBusy(false);
    }
  }

  if (editing) {
    return (
      <tr>
        <td>
          <input value={version} onChange={(e) => setVersion(e.target.value)} style={{ width: 90 }} />
        </td>
        <td>{new Date(release.released_at).toLocaleDateString()}</td>
        <td>{release.file_size ? `${(release.file_size / 1024 / 1024).toFixed(1)} MB` : "—"}</td>
        <td>{release.is_latest ? "✓" : ""}</td>
        <td>
          <div className="button-row">
            <button onClick={save} disabled={busy}>Save</button>
            <button className="secondary" onClick={() => setEditing(false)} disabled={busy}>Cancel</button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>{release.version}</td>
      <td>{new Date(release.released_at).toLocaleDateString()}</td>
      <td>{release.file_size ? `${(release.file_size / 1024 / 1024).toFixed(1)} MB` : "—"}</td>
      <td>{release.is_latest ? "✓" : ""}</td>
      <td>
        <div className="button-row">
          <input ref={fileInput} type="file" accept=".msi" style={{ display: "none" }}
                 onChange={(e) => replaceFile(e.target.files?.[0] ?? null)} />
          {!release.is_latest && (
            <button onClick={() => onSetLatest(release.id)} disabled={busy}>Mark latest</button>
          )}
          <button onClick={() => fileInput.current?.click()} disabled={busy}
                  title="Upload a new MSI for this version - keeps the version number and its latest flag">
            {replaceStatus ?? "Replace file"}
          </button>
          <button className="secondary" onClick={() => setEditing(true)} disabled={busy}>Edit</button>
          <button className="danger" onClick={remove} disabled={busy || release.is_latest} title={release.is_latest ? "Promote another version to latest first" : "Delete this version"}>
            Delete
          </button>
        </div>
      </td>
    </tr>
  );
}

function UploadForm({ onDone }: { onDone: () => void }) {
  const [version, setVersion] = useState("");
  const [markLatest, setMarkLatest] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) return;
    setSubmitting(true);
    setError(null);
    try {
      setStatus("Hashing file…");
      const sha256 = await sha256Hex(file);

      setProgress(0);
      setStatus("Uploading…");
      const filePath = await uploadMsiToR2(file, version, (pct, detail) => {
        setProgress(pct);
        setStatus(`Uploading… (${detail})`);
      });

      setStatus("Recording release…");
      const record = { version, filePath, fileSize: file.size, sha256, markLatest };
      try {
        await api.post("/admin/releases", record);
      } catch (err) {
        // Same version already recorded (a rebuild): the file in storage has just been overwritten
        // by this upload, so offer to point the existing release at it.
        const msg = err instanceof Error ? err.message : "";
        if (!msg.includes("already exists")) throw err;
        if (!window.confirm(`Version ${version} already exists. Replace its installer with the file you just uploaded?`)) {
          throw new Error("Upload finished but the release was not replaced - the existing version is unchanged.");
        }
        await api.post("/admin/releases", { ...record, replace: true });
      }

      setStatus(null);
      setProgress(0);
      setVersion("");
      setFile(null);
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
      setStatus(null);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="card">
      <label>
        Version (e.g. 1.4.2)
        <input required value={version} onChange={(e) => setVersion(e.target.value)} />
      </label>
      <label>
        MSI file
        <input
          type="file"
          accept=".msi"
          required
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
      </label>
      <label className="checkbox-label">
        <input type="checkbox" checked={markLatest} onChange={(e) => setMarkLatest(e.target.checked)} />
        Mark as latest
      </label>
      <button className="primary" type="submit" disabled={submitting || !file}>
        {submitting ? status ?? "Working…" : "Upload"}
      </button>
      {submitting && (
        <>
          <div className="progress" role="progressbar" aria-valuenow={progress} aria-label={status ?? "Uploading"}>
            <div className="progress-bar" style={{ width: `${progress}%` }} />
          </div>
          <p className="muted-subtitle">
            {status}
            {status?.startsWith("Uploading") ? ` ${progress}%` : ""}
          </p>
        </>
      )}
      {error && <p className="error-text">{error}</p>}
    </form>
  );
}
